import { Outlet, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { isNative } from '@/lib/platform';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

/**
 * Gates admin/trfer route trees on native. On web, renders Outlet so nested
 * routes resolve normally. On native, renders a friendly "use the website"
 * landing — never delegates to the nested admin/trfer UI, which is
 * desktop-density and not in the mobile v1 scope.
 *
 * Two recovery paths: "Back to dashboard" returns to the role-appropriate
 * home (default parent dashboard) without losing the session, while
 * "Sign out" remains as the fallback for users who actually want to
 * switch accounts. Without the back path, anyone who tapped an admin
 * deep link by mistake had to sign out + back in to recover (M1 smoke
 * test friction note).
 *
 * Used via the same `<Route element={<MobileRouteGuard />}>...</Route>`
 * wrapper pattern as RoleGatedRoute (see App.tsx).
 */
export function MobileRouteGuard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('feedback');

  if (!isNative()) return <Outlet />;

  const isTrfer = user?.role === 'trfer';

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center px-6 text-center">
      <h1 className="mb-3 text-2xl font-semibold">
        {t('mobileGuard.title')}
      </h1>
      <p className="mb-6 max-w-md text-stevens-ink-2">
        {isTrfer
          ? t('mobileGuard.trferBody')
          : (
            <Trans
              t={t}
              i18nKey="mobileGuard.adminBody"
              values={{ site: 'trfimagine.org' }}
              components={{ site: <span className="font-medium" translate="no" /> }}
            />
          )}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {isTrfer
          ? <Button onClick={() => navigate('/trfer/m')}>{t('mobileGuard.goToToday')}</Button>
          : <Button onClick={() => navigate('/dashboard')}>{t('mobileGuard.backToDashboard')}</Button>}
        <Button variant="outline" onClick={logout}>{t('mobileGuard.signOut')}</Button>
      </div>
    </div>
  );
}
